<template>
  <div v-if="links && links.length > 0" class="card border-0 shadow-sm">
    <div class="card-body p-4">
      <h3 class="h5 mb-4">Your Short Links</h3>
      <div class="table-responsive">
        <table class="table table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th style="min-width: 150px">Short URL</th>
              <th style="min-width: 100px">Type</th>
              <th style="min-width: 200px">Destination</th>
              <th style="width: 120px">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="link in links" :key="link.slug">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span class="text-truncate">{{ getFullShortUrl(link.slug) }}</span>
                  <button 
                    class="btn btn-sm btn-light"
                    @click="copyToClipboard(getFullShortUrl(link.slug))"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                </div>
              </td>
              <td>
                <span class="badge rounded-pill" :class="getLinkTypeBadgeClass(link.type)">
                  {{ link.type }}
                </span>
              </td>
              <td>
                <template v-if="link.type === 'redirect'">
                  <a 
                    :href="link.redirect_to" 
                    target="_blank" 
                    class="text-truncate d-inline-block text-decoration-none"
                    style="max-width: 300px;"
                  >
                    {{ link.redirect_to }}
                  </a>
                </template>
                <template v-else>
                  <span class="text-truncate d-inline-block" style="max-width: 300px;">
                    {{ link.file_id }}
                  </span>
                </template>
              </td>
              <td>
                <div class="d-flex gap-2">
                  <button 
                    class="btn btn-sm btn-outline-danger"
                    @click="$emit('delete', link)"
                    title="Delete"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                  <button 
                    v-if="!link.owner_id"
                    class="btn btn-sm btn-outline-success"
                    @click="$emit('claim', link.slug)"
                    title="Claim Ownership"
                  >
                    <i class="bi bi-person-check"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div v-else-if="!isLoading" class="text-center mt-4">
    <p class="text-muted">No short links created yet</p>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default defineComponent({
  name: 'ShortLinkTable',

  computed: {
    ...mapGetters('shortLinks', [
      'stateLinks',
      'stateIsLoading'
    ]),
    links() {
      return this.stateLinks || [];
    },
    isLoading() {
      return this.stateIsLoading;
    }
  },

  methods: {
    ...mapActions('shortLinks', [
      'getLinks',
      'deleteLink',
      'claimLink'
    ]),

    async confirmDelete(link) {
      if (confirm(`Are you sure you want to delete the short link: ${link.slug}?`)) {
        try {
          await this.deleteLink(link.slug);
          // The store will handle removing the link from the state
        } catch (error) {
          console.error('Error deleting link:', error);
        }
      }
    },

    async handleClaimLink(slug) {
      try {
        await this.claimLink(slug);
        // The store will handle updating the link ownership
      } catch (error) {
        console.error('Error claiming link:', error);
      }
    },

    getLinkTypeBadgeClass(type) {
      switch (type) {
        case 'file':
          return 'bg-info';
        case 'redirect':
          return 'bg-primary';
        default:
          return 'bg-secondary';
      }
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
        .then(() => {
        })
        .catch(err => {
          console.error('Failed to copy:', err);
        });
    },

    getFullShortUrl(slug) {
      return `${window.location.origin}/link/${slug}`;
    }
  },

  created() {
    // Load links and files when component is created
    this.getLinks();
  }
});
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.table td {
  padding: 1rem 0.75rem;
}

.table th {
  padding: 0.75rem;
  font-weight: 600;
  color: #6c757d;
}

.badge {
  font-weight: 500;
  padding: 0.5em 0.75em;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
}

.btn-light {
  background-color: #f8f9fa;
  border-color: #e9ecef;
}

.btn-light:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem !important;
  }
  
  .table td, 
  .table th {
    padding: 0.75rem 0.5rem;
  }
  
  .btn-sm {
    padding: 0.25rem 0.4rem;
  }
}
</style> 